import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';

import { AuthContext } from 'authentication/AuthContext';
import { DrawerLayoutWrapper } from 'common';
import { GT_ZERO } from 'utils/helpers/validationMessages';
import { createSnack } from 'snackbar/snackbarSlice';
import { useSnackbar } from 'notistack';
import {
  useGetNominationLegPeriodQuery,
  useGetNominationLegProductsQuery,
  useGetNominationsLegsQuery,
} from '../nominationsEndpoints';

const infoAlertStyles = {
  '& .MuiAlert-message': {
    py: 0,
  },
  alignItems: 'center',
  backgroundColor: 'success.lighter',
  borderRadius: '4px',
  boxShadow: 'none',
  py: 2,
};

const objLength = R.o(R.length, R.values);
const notEmpty = R.complement(R.isEmpty);

const Form = ({
  copiedNomination,
  nominationState,
  onClose,
  onSubmit,
  setNominationState,
  setInitialCopiedNominationState,
}) => {
  const dispatch = useDispatch();
  const [hasShownError, setHasShownError] = useState(false);
  const { nominationMonth, segment, shipper, products } = nominationState;
  const { closeSnackbar } = useSnackbar();

  const { affiliates } = useContext(AuthContext);
  const affiliateShippers = affiliates.filter(R.propEq('shipper', true));

  useEffect(() => {
    if (R.isNil(shipper) && affiliateShippers && R.equals(affiliateShippers.length, 1))
      setNominationState(
        R.mergeLeft({ hasOnePossibleShipper: true, shipper: affiliateShippers[0] })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliateShippers, shipper]);

  const companyCustomerIdName = R.prop('customerIdName', shipper);
  const dataCompanyId = R.prop('dataCompanyId', shipper);

  const {
    data: segmentQuery = [],
    isFetching: isFetchingSegments,
    isSuccess: isSuccessSegments,
    error: segmentsError,
  } = useGetNominationsLegsQuery(
    {
      shipper: { companyCustomerIdName, dataCompanyId },
    },
    { skip: !companyCustomerIdName || !dataCompanyId }
  );

  const nominationLegId = R.prop('id', segment);
  const periodId = R.prop('id', nominationMonth);

  const { data: nominationMonthsQuery = [], isSuccess: isSuccessNominationMonths } =
    useGetNominationLegPeriodQuery(
      {
        nominationLegId,
      },
      { skip: !nominationLegId }
    );

  const { data: productGradeQuery = [], isSuccess: isSuccessProductGradeQuery } =
    useGetNominationLegProductsQuery(
      {
        nominationLegId,
        periodId,
        shipper: { companyCustomerIdName, dataCompanyId },
      },
      { skip: R.or(R.isNil(nominationLegId), R.isNil(periodId)) }
    );

  const handleShipperChange = (_, value) => {
    setNominationState(
      R.mergeLeft({
        nominationMonth: { id: null, name: '', periodDate: '' },
        products: {},
        segment: null,
        shipper: value,
      })
    );
  };

  const handleSegmentChange = (_, value) => {
    setNominationState(
      R.mergeLeft({
        nominationMonth: { id: null, name: '', periodDate: '' },
        products: {},
        segment: value,
      })
    );
  };

  const [validationState, setValidationState] = useState({});

  const validateVolume = productId =>
    R.o(
      setValidationState,
      R.ifElse(
        R.isEmpty,
        // remove product from state
        R.always(R.dissoc(productId)),
        // include product in state
        R.compose(
          R.mergeLeft,
          R.objOf(productId),
          R.ifElse(R.lte(R.__, 0), R.always(GT_ZERO), R.always(''))
        )
      )
    );

  const handleVolumeChange = (productId, name) =>
    R.compose(
      setNominationState,
      R.ifElse(
        R.isEmpty,
        // remove product from state
        R.always(R.dissocPath(['products', productId])),
        // include product in state
        R.compose(
          R.assocPath(['products', productId]),
          R.assoc('volume', R.__, { name, productId }),
          v => v.toLocaleString(),
          v => parseInt(v)
        )
      ),
      R.tap(validateVolume(productId)),
      R.replace(/\D/g, ''),
      R.path(['target', 'value'])
    );

  const isCopy = R.not(R.isNil(copiedNomination));

  // Specific product grade copies only its matching product
  const copiedProductName = R.propOr('', 'productName', copiedNomination);
  const isSpecificGrade = isCopy && R.not(R.equals('all', copiedProductName));
  const productList = isCopy ? R.values(products) : productGradeQuery;

  const allProductVolumesFilled = productList.every(({ productId }) =>
    notEmpty(R.pathOr('', [productId, 'volume'], products))
  );

  const isInvalid = [
    R.isEmpty(R.prop('id', shipper)),
    R.isNil(segment),
    R.isNil(nominationMonth.id),
    R.any(notEmpty, R.values(validationState)),
    isCopy ? R.not(allProductVolumesFilled) : R.lt(objLength(products), 1),
  ].includes(true);

  const [cloneComplete, setCloneComplete] = useState(false);
  useEffect(() => {
    if (R.not(R.isNil(copiedNomination))) {
      try {
        const seg = segmentQuery.find(s => s.name === copiedNomination.segmentName);
        const month = nominationMonthsQuery[0];

        setNominationState(
          R.mergeLeft({
            nominationMonth: month || { id: null, name: '', periodDate: '' },
            segment: seg || {},
            shipper: affiliateShippers.find(
              s => s.customerNameShort === copiedNomination.shipperName
            ),
          })
        );
        setCloneComplete(
          isSuccessSegments && isSuccessNominationMonths && isSuccessProductGradeQuery
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Could not copy Nomination', copiedNomination);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedNomination, isSuccessSegments, isSuccessNominationMonths, isSuccessProductGradeQuery]);

  // seed every product field once the grade data arrives so pre-filled values count as entered
  const [hasSettedCopyVolumes, sethasSettedCopyVolumes] = useState(false);
  useEffect(() => {
    if (
      !isCopy ||
      hasSettedCopyVolumes ||
      !isSuccessProductGradeQuery ||
      R.isEmpty(productGradeQuery)
    )
      return;

    const productsToSeed = isSpecificGrade
      ? productGradeQuery.filter(({ name }) => name === copiedProductName)
      : productGradeQuery;

    setNominationState(
      R.assoc(
        'products',
        productsToSeed.reduce(
          (acc, { productId, name }) => ({
            ...acc,
            [productId]: {
              name,
              productId,
              volume: Number(copiedNomination.volNominated).toLocaleString(),
            },
          }),
          {}
        )
      )
    );
    sethasSettedCopyVolumes(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCopy, hasSettedCopyVolumes, isSuccessProductGradeQuery, productGradeQuery]);

  const [hasRunSccessfully, setHasRunSuccessfully] = useState(false);
  useEffect(() => {
    if (cloneComplete && hasSettedCopyVolumes && !hasRunSccessfully) {
      setInitialCopiedNominationState({ ...nominationState });
      setHasRunSuccessfully(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneComplete, hasSettedCopyVolumes, nominationState, setInitialCopiedNominationState]);

  useEffect(() => {
    if (segmentsError?.status === 403 && !hasShownError) {
      setHasShownError(true);
      dispatch(
        createSnack({
          message: 'You do not have sufficient permissions to edit, elect or copy Nominations.',
          options: { autoHideDuration: 10000, persist: false, variant: 'error' },
        })
      );
    }
  }, [segmentsError, dispatch, hasShownError, closeSnackbar]);

  return (
    <DrawerLayoutWrapper
      buttons={
        <Button disabled={isInvalid} onClick={onSubmit} sx={{ width: '159px' }}>
          Submit
        </Button>
      }
      onHeaderButtonClick={onClose}
      title={`${R.isNil(copiedNomination) ? 'New' : 'Copy'} Monthly Nomination`}
    >
      {R.not(R.isNil(copiedNomination)) && !cloneComplete ? (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          // eslint-disable-next-line
          minHeight={{ xs: '266px', sm: '174px' }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mt: 3, mx: 2 }}>
          {R.gt(affiliateShippers.length, 1) && (
            <Autocomplete
              disableClearable
              getOptionLabel={R.prop('customerNameShort')}
              isOptionEqualToValue={R.eqProps('customerIdName')}
              onChange={handleShipperChange}
              options={affiliateShippers}
              renderInput={inputParams => <TextField {...inputParams} label="Shipper" />}
              value={shipper}
            />
          )}
          <Autocomplete
            disableClearable
            disabled={!isSuccessSegments}
            getOptionLabel={R.prop('name')}
            isOptionEqualToValue={R.eqProps('id')}
            loading={isFetchingSegments}
            onChange={handleSegmentChange}
            options={segmentQuery}
            renderInput={inputParams => <TextField {...inputParams} label="Segment" />}
            value={segment}
          />
          <FormControl sx={{ mb: 1 }}>
            <FormLabel id="nomination-month-group" sx={{ mb: 2 }}>
              Nomination Month
            </FormLabel>
            {R.or(R.isEmpty(nominationMonthsQuery), R.isNil(segment)) ? (
              <Alert
                icon={<InfoRoundedIcon sx={{ color: 'graphic.main' }} />}
                severity="info"
                sx={infoAlertStyles}
              >
                <Typography sx={{ color: 'text.secondary' }} variant="body2">
                  Select a segment to see available nomination periods.
                </Typography>
              </Alert>
            ) : (
              <RadioGroup
                aria-labelledby="nomination-month-group"
                onChange={(_, value) => {
                  setNominationState(
                    R.assoc(
                      'nominationMonth',
                      nominationMonthsQuery.find(month => month.id === parseInt(value))
                    )
                  );
                }}
                value={nominationMonth.id}
              >
                {nominationMonthsQuery.map(({ id, name }) => (
                  <FormControlLabel control={<Radio />} key={id} label={name} value={id} />
                ))}
              </RadioGroup>
            )}
          </FormControl>

          {productList.map(({ productId, name, volume }) => (
            <FormControl key={productId}>
              <FormLabel sx={{ mb: 2 }}>{name}</FormLabel>

              <TextField
                disabled={R.and(R.isNil(copiedNomination), R.not(R.isNil(volume)))}
                error={R.not(R.isEmpty(R.propOr('', productId, validationState)))}
                helperText={R.prop(productId, validationState)}
                label="Product Volume"
                onChange={handleVolumeChange(productId, name)}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
                  },
                }}
                sx={{ mb: 3 }}
                value={R.pathOr(R.defaultTo('', volume), [productId, 'volume'], products)}
              />
            </FormControl>
          ))}
        </Stack>
      )}
    </DrawerLayoutWrapper>
  );
};

Form.propTypes = {
  copiedNomination: PropTypes.shape({
    nomMonth: PropTypes.string.isRequired,
    productName: PropTypes.string.isRequired,
    segmentId: PropTypes.string.isRequired,
    segmentName: PropTypes.string.isRequired,
    shipperName: PropTypes.string.isRequired,
    volAccepted: PropTypes.number.isRequired,
    volAwarded: PropTypes.number.isRequired,
    volNominated: PropTypes.number.isRequired,
  }),
  nominationState: PropTypes.shape({
    nominationMonth: PropTypes.shape({
      id: PropTypes.number,
    }),
    products: PropTypes.shape({}),
    segment: PropTypes.shape({}),
    shipper: PropTypes.shape({}),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setInitialCopiedNominationState: PropTypes.func.isRequired,
  setNominationState: PropTypes.func.isRequired,
};

Form.defaultProps = {
  copiedNomination: null,
};

export default Form;
